import { motion } from "framer-motion";
import { ArrowUpRight, Download, Image as ImageIcon, Images, PenLine, Package, CreditCard } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { usePageMeta } from "../hooks/usePageMeta";

const EASE = [0.16, 1, 0.3, 1];

const resources = [
  {
    icon: ImageIcon,
    meta: "8 files · PNG",
    title: "Logo Pack",
    copy: "Print-quality PNGs (4000px) — horizontal and stacked layouts, light and dark versions, transparent and solid backgrounds. For business cards, promotional items, and print.",
    action: "Download ZIP",
    href: "/assets/downloads/nwta-logo-pack.zip",
    download: true,
    thumbs: [
      { src: "/assets/downloads/thumbs/logo-horizontal-dark.jpg", alt: "Horizontal dark logo layout" },
      { src: "/assets/downloads/thumbs/logo-stacked-light.jpg", alt: "Stacked light logo layout" },
    ],
  },
  {
    icon: Images,
    meta: "8 files · PNG",
    title: "AXIOM Product Logos",
    copy: "Individual product lockups for every AXIOM edition — Underwriter, Lenders, Agents, Attorneys, National Agencies, and Commercial — plus the TrueNorth and ART (AI Rendered Title) logos. For decks, one-pagers, and product-specific marketing.",
    action: "Download ZIP",
    href: "/assets/downloads/nwta-product-logos.zip",
    download: true,
    thumbs: [
      { src: "/assets/downloads/thumbs/product-logos-commercial.jpg", alt: "AXIOM for Commercial logo" },
      { src: "/assets/downloads/thumbs/product-logos-art.jpg", alt: "ART — AI Rendered Title logo" },
    ],
  },
  {
    icon: Images,
    meta: "3 files · PNG",
    title: "Social Banners",
    copy: "Cover images sized for each platform: Facebook Page (1640×624), LinkedIn Company Page (2256×382), and LinkedIn personal profile (1584×396, logo offset for your photo).",
    action: "Download ZIP",
    href: "/assets/downloads/nwta-social-banners.zip",
    download: true,
    thumbs: [
      { src: "/assets/downloads/thumbs/banner-facebook.jpg", alt: "Facebook page cover" },
      { src: "/assets/downloads/thumbs/banner-linkedin.jpg", alt: "LinkedIn company page cover" },
      { src: "/assets/downloads/thumbs/banner-personal.jpg", alt: "LinkedIn personal profile cover" },
    ],
  },
  {
    icon: CreditCard,
    meta: "3 files · PNG · 300 DPI",
    title: "Business Cards",
    copy: "Print-ready 3.5×2 in cards with bleed: a signature-style front (universal logo version plus a personalized sample) and a navy back with a QR code that links straight to the site. Upload directly to MOO or Vistaprint.",
    action: "Open generator",
    href: "/card-generator",
    download: false,
    thumbs: [
      { src: "/assets/downloads/thumbs/card-front-sample.jpg", alt: "Business card front — signature style" },
      { src: "/assets/downloads/thumbs/card-back-qr.jpg", alt: "Business card back with QR code" },
    ],
  },
  {
    icon: PenLine,
    meta: "Web tool",
    title: "Email Signature Generator",
    copy: "Type your name, title, email, and phone — then copy a fully formatted signature into Gmail, Outlook, or Apple Mail. Always copy from this live page so the logo link stays permanent.",
    action: "Open generator",
    href: "/signature",
    download: false,
  },
  {
    icon: Package,
    meta: "ZIP · HTML + PNG + README",
    title: "Email Signature Kit (offline)",
    copy: "The same generator as a downloadable ZIP with the logo file and step-by-step install instructions for each email client — handy to send to teammates.",
    action: "Download ZIP",
    href: "/assets/downloads/nwta-email-signature-kit.zip",
    download: true,
  },
];

const Resources = () => {
  usePageMeta({ title: "Team Resources & Brand Assets", description: "Logos, social banners, business cards, and email signatures for the Northwest Tech Alliance team.", path: "/resources" });
  return (
  <main className="bg-navy min-h-screen">
    <Navbar />
    <section className="relative pt-36 md:pt-44 pb-24 md:pb-32 grain" data-testid="resources-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14 md:mb-20"
        >
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">Team Resources</span>
          <h1 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog mt-6">
            Brand assets<span className="text-tech">.</span>
          </h1>
          <p className="text-base md:text-lg text-fog/60 leading-relaxed max-w-2xl mt-6">
            Everything the team needs to represent Northwest Tech Alliance — logos for
            print and digital, platform-sized social covers, and ready-to-paste email signatures.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {resources.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.article
                key={r.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: EASE }}
                className="group relative border border-steel/30 bg-navy-deep/60 p-8 md:p-10 flex flex-col overflow-hidden hover:border-tech/70 transition-colors duration-500"
                data-testid={`resource-card-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="absolute top-0 left-0 h-[2px] w-0 bg-tech group-hover:w-full transition-[width] duration-700 ease-out" />
                <div className="flex items-start justify-between mb-6">
                  <Icon className="h-6 w-6 text-tech-bright" strokeWidth={1.5} />
                  <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border border-steel/60 text-fog/50">
                    {r.meta}
                  </span>
                </div>
                <h2 className="font-display font-semibold tracking-tight text-2xl text-fog mb-3">{r.title}</h2>
                {r.thumbs && r.thumbs.length > 0 && (
                  <div className={`grid gap-2 mb-5 ${r.thumbs.length > 1 ? "grid-cols-1" : ""}`} data-testid={`resource-thumbs-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {r.thumbs.map((t) => (
                      <div key={t.src} className="border border-steel/25 overflow-hidden">
                        <img
                          src={t.src}
                          alt={t.alt}
                          loading="lazy"
                          className="w-full h-auto object-cover group-hover:opacity-95 transition-opacity duration-300"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-fog/60 leading-relaxed mb-8">{r.copy}</p>
                <a
                  href={r.href}
                  {...(r.download ? { download: "" } : {})}
                  data-testid={`resource-action-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="mt-auto inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.22em] uppercase text-fog/50 group-hover:text-tech-bright transition-colors duration-300"
                >
                  {r.action}
                  {r.download ? (
                    <Download className="h-4 w-4 group-hover:translate-y-0.5 transition-transform duration-300" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  )}
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
    <Footer />
  </main>
  );
};

export default Resources;
